import React from 'react';
import { css } from '@emotion/core';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';

import { SLOGraphError } from '@/controllers';
import { SLOGraphErrorActions } from '@/controllers/slo-graph-controller/enums';
import {
  DAC_TROUBLESHOOTING_DOC_LINK,
  SLACK_HELP_CHANNEL_LINK,
  DAC_FAQS_DOC_LINK,
} from '@/constants';
import { SLOGraphWarning } from '@/controllers/slo-graph-controller/warnings';
import ExternalLinkAction from '@/components/primitive/external-link';
import { RerunCollectionButton } from '@/components/primitive/rerun-collection';
import { PerformanceWithEvents } from '@/ui';

import Log from './log';
import { formatError } from './utils';

// NOTE: Truncate the message to 4 lines, can be replaced with `line-clamp` when
// supported: https://caniuse.com/#search=line-clamp.
export const errorMessageStyles = css`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const getActions = (
  handleOnSloEdit: () => void,
  errorLog: SLOGraphError['log'],
  publicId: string,
  performance?: PerformanceWithEvents,
  warnings?: SLOGraphWarning[]
) => ({
  [SLOGraphErrorActions.AddQueries]: (
    <SectionMessageAction key="add-queries" onClick={handleOnSloEdit}>
      Add queries
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.UpdateQueries]: (
    <SectionMessageAction key="update-queries" onClick={handleOnSloEdit}>
      Update queries
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.RerunCollection]: <RerunCollectionButton publicId={publicId} />,
  // NOTE: The 'slo-graph-controller' → `generateApiGraphErrorConfig` util
  // simply won't display this action if the API returns an empty
  // `indicator.failures` array.
  [SLOGraphErrorActions.SeeErrorLog]: (
    <SectionMessageAction key="see-error-log">
      {errorLog || warnings ? <Log errorLog={errorLog} warnings={warnings} /> : null}
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.SeeSlack]: (
    <SectionMessageAction key="see-help-tome">
      <ExternalLinkAction text="Get help in Slack" href={SLACK_HELP_CHANNEL_LINK} />
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.InvalidQueryOrBadInstrumentation]: (
    <SectionMessageAction key="invalid-query-or-bad-instrumentation">
      <ExternalLinkAction
        text="Find out how to resolve this error"
        href={`${DAC_TROUBLESHOOTING_DOC_LINK}#invalid-signalflow-query-or-bad-instrumentation`}
      />
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.MultipleTimeSeries]: (
    <SectionMessageAction key="multiple-timeseries">
      <ExternalLinkAction
        text="Find out how to resolve this error"
        href={`${DAC_TROUBLESHOOTING_DOC_LINK}#unexpected-multiple-time-series-data-points`}
      />
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.FractionalValue]: (
    <SectionMessageAction key="fractional-value">
      <ExternalLinkAction
        text="Find out how to resolve this error"
        href={`${DAC_TROUBLESHOOTING_DOC_LINK}#unexpected-fractional-value-in-time-series-data-points`}
      />
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.NegativeValue]: (
    <SectionMessageAction key="negative-value">
      <ExternalLinkAction
        text="Find out how to resolve this error"
        href={`${DAC_TROUBLESHOOTING_DOC_LINK}#unexpected-negative-value-in-time-series-data-points`}
      />
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.DatapointTimestampsDidNotAlign]: (
    <SectionMessageAction key="datapoint-timestamps-did-not-align">
      <ExternalLinkAction
        text="Find out how to resolve this error"
        href={`${DAC_TROUBLESHOOTING_DOC_LINK}#data-point-timestamps-for-good-and-bad-events-did-not-align`}
      />
    </SectionMessageAction>
  ),
  // The user has put fill(0) in their queries, this is probably due to transient issue with SFx
  // @see https://hello.atlassian.net/browse/TOME-740
  [SLOGraphErrorActions.DatapointTimestampsDidNotAlignWithFilledQueries]: (
    <RerunCollectionButton
      publicId={publicId}
      shouldDeleteOldDatapoints
      performance={performance}
    />
  ),
  [SLOGraphErrorActions.NoData]: (
    <SectionMessageAction key="no-data">
      <ExternalLinkAction
        text="Find out how to resolve this error"
        href={`${DAC_TROUBLESHOOTING_DOC_LINK}#no-data-for-compliance-period`}
      />
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.NoDataGlanceTypeStaging]: (
    <SectionMessageAction key="no-data-glance-type-staging">
      <ExternalLinkAction
        text="Find out how to resolve this error"
        href={`${DAC_FAQS_DOC_LINK}glance-slos/#why-the-glance-slo-i-created-in-the-staging-environment-does-not-have-any-data-`}
      />
    </SectionMessageAction>
  ),
  [SLOGraphErrorActions.NoDataGlanceType]: (
    <SectionMessageAction key="no-data-glance-type">
      <ExternalLinkAction
        text="Find out more details about this"
        href={`${DAC_FAQS_DOC_LINK}glance-slos/#how-long-does-it-take-to-view-data-for-newly-created-glance-slos-`}
      />
    </SectionMessageAction>
  ),
});

interface IProps {
  error: SLOGraphError;
  warnings?: SLOGraphWarning[];
  onSloEdit: () => void;
  publicId: string;
  performance?: PerformanceWithEvents;
}

const FaultZone: React.FC<IProps> = ({ error, warnings, onSloEdit, publicId, performance }) => {
  const actions = error.actions.map(
    action => getActions(onSloEdit, error.log, publicId, performance, warnings)[action]
  );

  return (
    <SectionMessage
      testId="slo-card-graph-fault-zone"
      actions={actions}
      appearance="warning"
      title={error.title}
    >
      <p css={[errorMessageStyles]}>{formatError(error.error)}</p>
    </SectionMessage>
  );
};

export default FaultZone;
